import { useEffect, useMemo, useState } from 'react';
import { fetchIncomingFiles } from '../services/incomingFiles.api';
import { buildOptions, filterRows, transformRow, todayStr } from '../utils/incomingFiles.helpers';

const makeDefaultRange3 = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 2);
  const iso = (d) => d.toISOString().split('T')[0];
  return { start: iso(start), end: iso(end) };
};

export function useIncomingFiles() {
  const [loading, setLoading] = useState(true);
  const [baseRows, setBaseRows] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusView, setStatusView] = useState(['Fail']);
  const [subStatusView, setSubStatusView] = useState([]);
  const [filters, setFilters] = useState({ fileName: [], dataProvider: [] });
  const [dateRange, setDateRange] = useState(makeDefaultRange3());

  const options = useMemo(() => buildOptions(baseRows), [baseRows]);

  const rows = useMemo(
    () => filterRows(baseRows, { filters, searchQuery, statusView, subStatusView }),
    [baseRows, filters, searchQuery, statusView, subStatusView]
  );

  // If sub-statuses are selected, send the first one to the API (SP handles exact match).
  // Otherwise fall back to the top-level status (e.g. 'Fail' returns all Fail - * rows from SP).
  const apiStatusView =
    subStatusView.length === 1
      ? subStatusView[0]
      : statusView.length === 1 && statusView[0] !== 'All'
      ? statusView[0]
      : null;
  const statusViewKey = [...statusView, ...subStatusView].join(',');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetchIncomingFiles({ start: dateRange.start, end: dateRange.end, statusView: apiStatusView });
        if (!alive) return;
        const transformed = Array.isArray(res) ? res.map(transformRow) : [];
        setBaseRows(transformed);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end, statusViewKey]);

  return {
    loading,
    rows,
    searchQuery, setSearchQuery,
    dateRange, setDateRange,
    statusView, setStatusView,
    subStatusView, setSubStatusView,
    filters, setFilters,
    fileNameOptions: options.fileNameOptions,
    dataProviderOptions: options.dataProviderOptions,
    today: todayStr(),
  };
}
